import { useMemo, useState, useEffect } from 'react';
import { Button, Col, Form, FloatingLabel, Modal, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useCreateOrUpdateCatch, useDeleteCatch } from 'src/model/catches';
import { useWorkspace } from 'src/model/workspaces';
import { SpinnerButton } from 'src/components/base/Elements';
import { useModalDialog, withModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import { useCreateCatchImage, useDeleteCatchImage, useFetchCatchImages } from 'src/model/images';
import { ImageUploadDialog } from 'src/components/trap/ImageUploadDialog';
import { ImageGalleryPanel } from 'src/components/base/ImageGalleryPanel';
import { DatePickerControl } from 'src/components/base/DatePicker';
import { LocationDialog } from 'src/components/trap/LocationDialog';
import { useUpdateEffect, useEffectState } from 'src/utils/hooks';
import { useAction } from 'src/hooks/action';
import { validatorResolver } from 'src/utils/validator';
import { parseISO, formatISO, formatFromISO } from 'src/utils/datefns';
import { isValidPosition } from 'src/utils/helpers';
import { downloadCatchImagesZip } from 'src/api/api2';
import { downloadCatchImageFile } from 'src/utils/download';
import { useSystemStore } from 'src/stores';
import { OTHER_ANIMAL } from 'src/constants';
import classes from './CatchDialog.module.scss';

const MAX_IMAGES = 3;

export const CatchDialog = withModalDialog(({
  trap_id,
  catchItem,
  mutate,
  onClose,
  editable = true,
  allowUpload = true,
  allowDeletePhoto = true,
  allowDownload = true,
  showDeleteAlarm = false,
  deleteLabel = __('Delete catch'),
}) => {
  const { animals = [] } = useWorkspace();
  const { token } = useSystemStore();
  const animalTypes = useMemo(() => [...animals, { type: OTHER_ANIMAL, title: __('Other') }], [animals]);

  const event = catchItem ?? {};
  const catch_id = event.catch_id;
  const history_id = event.history_id;
  const isExisting = !!catch_id || !!history_id;
  const timeLocked = !!history_id || !!catch_id;

  const defaultValues = useMemo(() => ({
    ...event,
    animal_other: event?.animal_other ?? event?.other_animal ?? '',
    animal: !!(event?.animal_other ?? event?.other_animal) ? OTHER_ANIMAL : (event?.animal ?? animalTypes[0]?.type),
    time: event?.time ? parseISO(event.time) : new Date(),
  }), [event, animalTypes]);

  const { handleSubmit, register, control, getValues, watch, setValue, formState: { errors } } = useForm({
    resolver: validatorResolver({
      notes: 'string', time: 'required',
      animal: 'required', animal_other: { predicate: (value, { animal }) => (animal === OTHER_ANIMAL && !value) ? 'INVALID_ANIMAL' : '' },
      age: 'required', sex: 'required', bait: 'string',
      latitude: { latitude: 'longitude' }, longitude: { longitude: 'latitude' },
    }),
    defaultValues,
  });

  const { data: initialCatchImages, isLoading: isLoadingCatchImages, mutate: mutateCatchImages } = useFetchCatchImages({ id: catch_id });
  const [catchImages, setCatchImages] = useEffectState(initialCatchImages, [initialCatchImages]);
  const [deletedImages, setDeletedImages] = useState([]);

  const [createCatchImageAction] = useCreateCatchImage(mutateCatchImages);
  const [deleteCatchImageAction] = useDeleteCatchImage(mutateCatchImages);
  const [createOrUpdateCatchAction] = useCreateOrUpdateCatch();
  const [deleteCatchAction] = useDeleteCatch();

  const locationModal = useModalDialog();
  const deleteModal = useModalDialog();
  const imageUploadModal = useModalDialog();

  const { ages, genders } = config.catch;
  const animalType = watch('animal');

  useUpdateEffect(() => { if (animalType !== OTHER_ANIMAL) { setValue('animal_other', '') } }, [animalType]);

  useEffect(() => () => {
    catchImages.forEach(image => image.file && URL.revokeObjectURL(image.file_url));
  }, []);

  const title = editable
    ? (isExisting ? __('Edit catch') : __('Register Catch'))
    : __('Catch details');
  const subtitle = [event.trap_name, event.time ? formatFromISO(event.time, 'Pp') : ''].filter(Boolean).join(' · ');

  async function handleChangeLocation() {
    const [lat, lng] = getValues(['latitude', 'longitude']);
    const location = isValidPosition({ lat, lng }) && { lat: +lat, lng: +lng };
    const result = await locationModal.show({ title: __('Catch'), location });
    if (result) {
      setValue('latitude', result.lat);
      setValue('longitude', result.lng);
    }
  }

  async function handleAddImage() {
    await imageUploadModal.show({
      createImage: async (file) => {
        const url = URL.createObjectURL(file);
        setCatchImages([...catchImages, { thumb_url: url, file_url: url, name: file.name, file }]);
      }
    });
  }

  async function handleDeleteImage(image) {
    if (image.id !== undefined) {
      setDeletedImages((prev) => [...prev, image]);
      setCatchImages((prev) => prev.filter((item) => item.id !== image.id));
      return;
    }
    if (image?.file && image.file_url) {
      URL.revokeObjectURL(image.file_url);
    }
    setCatchImages((prev) => prev.filter((item) => item !== image));
  }

  async function handleDeleteClick() {
    const text = showDeleteAlarm
      ? __('Do you want to delete this Event?')
      : __('Do you want to delete this Catch?');
    if (await deleteModal.show({ text })) {
      if (history_id) {
        await deleteCatchAction({ history_id });
      } else {
        await deleteCatchAction({ catch_id });
      }
      mutate?.();
      onClose();
    }
  }

  async function handleDownloadImage(image, index) {
    await downloadCatchImageFile({ catchId: catch_id, image, index, token });
  }

  async function handleDownloadAll() {
    if (!catch_id) { return; }
    await downloadCatchImagesZip({ id: catch_id }, token);
  }

  const [submitAction, isSaving] = useAction(async (data) => {
    const payload = {
      trap_id: trap_id ?? event.trap_id,
      history_id,
      catch_id,
      ...data,
      time: formatISO(data.time),
    };
    const result = await createOrUpdateCatchAction(payload);
    if (result.error) { return; }

    const imageCatchId = result.data?.catch_id ?? catch_id;
    if (!imageCatchId) { return; }

    await Promise.all([
      ...deletedImages.map(image => deleteCatchImageAction({ id: imageCatchId, image_id: image.id })),
      ...catchImages.filter(image => image.id === undefined).map(image => createCatchImageAction({ id: imageCatchId, file: image.file }))
    ]);

    mutate?.();
    onClose();
  });

  return (
    <Form onSubmit={handleSubmit(submitAction)} role="form">
      <Modal.Header closeButton>
        <div>
          <Modal.Title>{title}</Modal.Title>
          {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className={classes.columns}>
          <div className={classes.pane}>
            <Row>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="animal">
                      <FloatingLabel label={__('Animal')}>
                        <Form.Select aria-label={__('Animal')} {...register('animal')} disabled={!editable}>
                          {animalTypes.map(({ type, title: animalTitle }) => <option key={type} value={type}>{animalTitle}</option>)}
                        </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="time">
                      <Controller
                        name="time"
                        control={control}
                        render={({ field }) => (
                          <DatePickerControl
                            {...field}
                            label={__('Time')}
                            showTimeInput
                            dateFormat="Pp"
                            disabled={timeLocked || !editable}
                            wrapperClassName={classes.datePickerWrapper}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">{__('Wrong date')}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="animal_other">
                  <FloatingLabel label={__('Other Animal')}>
                    <Form.Control
                      type="text"
                      placeholder={__('Other Animal')}
                      {...register('animal_other')}
                      disabled={!editable || animalType !== OTHER_ANIMAL}
                      isInvalid={!!errors.animal_other}
                    />
                    <Form.Control.Feedback type="invalid">{__('Other Animal is incorrect')}</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>

                <Row>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="age">
                      <FloatingLabel label={__('Age')}>
                        <Form.Select aria-label={__('Age')} {...register('age')} disabled={!editable}>
                          {ages.map(({ type, title: ageTitle }) => <option key={type} value={type}>{ageTitle}</option>)}
                        </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="sex">
                      <FloatingLabel label={__('Sex')}>
                        <Form.Select aria-label={__('Sex')} {...register('sex')} disabled={!editable}>
                          {genders.map(({ type, title: sexTitle }) => <option key={type} value={type}>{sexTitle}</option>)}
                        </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="bait">
                  <FloatingLabel label={__('Bait')}>
                    <Form.Control type="text" placeholder={__('Bait')} {...register('bait')} disabled={!editable} />
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3" controlId="notes">
                  <FloatingLabel label={__('Notes')}>
                    <Form.Control as="textarea" style={{ height: '6rem' }} placeholder={__('Notes')} {...register('notes')} disabled={!editable} />
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Button variant="primary" className="w-100" onClick={handleChangeLocation} disabled={!editable}>
                    <i className="bi bi-geo-alt me-1" /> {__('Change Catch Location on Map')}
                  </Button>
                </Form.Group>
          </div>

          <div className={classes.pane}>
            <ImageGalleryPanel
              images={catchImages}
              maxImages={MAX_IMAGES}
              allowUpload={editable && allowUpload}
              allowDeletePhoto={editable && allowDeletePhoto}
              allowDownload={allowDownload}
              showDownloadAll={false}
              onAddImage={handleAddImage}
              onDeleteImage={handleDeleteImage}
              onDownloadImage={handleDownloadImage}
              onDownloadAll={handleDownloadAll}
              isLoading={isLoadingCatchImages}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        {isExisting && (
          <Button variant="danger" onClick={handleDeleteClick}>
            {showDeleteAlarm ? __('Delete Alarm') : deleteLabel}
          </Button>
        )}
        <div className="flex-fill" />
        <Button variant="secondary" onClick={() => onClose()}>{editable ? __('Cancel') : __('Close')}</Button>
        {editable && (
          <SpinnerButton type="submit" animated={isSaving} disabled={isLoadingCatchImages}>
            {isExisting ? __('Save Changes') : __('Register new Catch')}
          </SpinnerButton>
        )}
      </Modal.Footer>

      <LocationDialog {...locationModal.register()} />
      <ConfirmModalDialog {...deleteModal.register()} />
      <ImageUploadDialog {...imageUploadModal.register()} />
    </Form>
  );
}, { size: 'xl' });
